/**
 * Auth Service - Real PHP backend authentication
 * Connects to /auth-api proxy → MVP patent/api/auth.php
 */

const AUTH_API = '/auth-api';

// ─── Types ────────────────────────────────────────
export interface AuthUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  display_name: string;
  avatar_url: string | null;
  is_global_admin: boolean;
  preferences: Record<string, any> | null;
}

export interface AuthOrganization {
  id: number;
  name: string;
  slug: string;
  license_type: string;
  license_expires_at: string;
}

export interface AuthRole {
  id: number;
  name: string;
  slug: string;
  role_type: string;
}

export interface LoginResponse {
  success: boolean;
  token?: string;
  user?: AuthUser;
  organization?: AuthOrganization | null;
  roles?: AuthRole[];
  permissions?: Record<string, string[]>;
  expires_at?: string;
  error?: string;
}

export interface SessionResponse {
  success: boolean;
  user?: AuthUser;
  organization?: AuthOrganization | null;
  roles?: AuthRole[];
  permissions?: Record<string, string[]>;
  error?: string;
}

export interface OrgUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_active: number;
  is_global_admin: number;
  last_login: string | null;
  created_at: string;
  phone: string | null;
  avatar_url: string | null;
  roles: string | null;
  role_slugs: string | null;
}

export interface AuditEntry {
  id: number;
  user_id: number | null;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  organization_id: number | null;
  org_name?: string;
  action: string;
  entity_type: string;
  entity_id: number | null;
  entity_name: string | null;
  old_values: string | null;
  new_values: string | null;
  details: string | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  source?: 'rbac' | 'grc'; // Which audit table the entry came from
}

export interface Organization {
  id: number;
  name: string;
  slug: string;
  description: string;
  is_active: number;
  contact_email: string | null;
  contact_phone: string | null;
  license_type: string;
  license_expires_at: string | null;
  max_users: number;
  openai_api_key: string | null;
  openai_model: string | null;
  user_count: number;
  created_at: string;
}

// Backward compat types
export interface User {
  id: number;
  username: string;
  role: 'admin' | 'editor' | 'viewer';
  permissions: Record<string, boolean>;
}

export interface AuthResponse {
  token: string;
  user: User;
  error?: string;
  passwordExpired?: boolean;
}

// ─── Storage keys ─────────────────────────────────
const TOKEN_KEY = 'app_token';
const USER_KEY = 'app_user';
const ORG_KEY = 'app_org';
const ROLES_KEY = 'app_roles';
const PERMS_KEY = 'app_permissions';

// ─── Helper ───────────────────────────────────────
async function authFetch<T>(action: string, opts: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem(TOKEN_KEY);
  const url = `${AUTH_API}?action=${action}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts.headers as Record<string, string> || {}),
  };
  if (token) headers['X-Session-Token'] = token;

  const response = await fetch(url, { ...opts, headers });
  const data = await response.json();

  if (!response.ok && response.status === 401) {
    // During login, pass the real server error (wrong password, etc.)
    if (action === 'login') {
      throw new Error(data.error || 'Invalid email or password');
    }
    // For all other endpoints: session expired → clear & redirect
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(ORG_KEY);
    localStorage.removeItem(ROLES_KEY);
    localStorage.removeItem(PERMS_KEY);
    throw new Error('Session expired');
  }

  if (!response.ok) {
    throw new Error(data.error || `HTTP ${response.status}`);
  }

  return data;
}

// ─── Auth Service ─────────────────────────────────
export const authService = {
  // ═══ AUTHENTICATION ═══════════════════════════
  login: async (email: string, password: string): Promise<LoginResponse & AuthResponse> => {
    try {
      const data = await authFetch<LoginResponse>('login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      if (data.success && data.token && data.user) {
        localStorage.setItem(TOKEN_KEY, data.token);
        // Store as AuthUser
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        if (data.organization) localStorage.setItem(ORG_KEY, JSON.stringify(data.organization));
        if (data.roles) localStorage.setItem(ROLES_KEY, JSON.stringify(data.roles));
        if (data.permissions) localStorage.setItem(PERMS_KEY, JSON.stringify(data.permissions));
        
        // Return both new + old format
        const role: 'admin' | 'editor' | 'viewer' = data.user.is_global_admin ? 'admin'
          : (data.roles?.some(r => r.role_type === 'org_admin') ? 'admin' : 'editor');
        const backCompatUser: User = {
          id: data.user.id,
          username: data.user.email,
          role,
          permissions: {},
        };
        localStorage.setItem('app_user_compat', JSON.stringify(backCompatUser));
      }

      return {
        ...data,
        token: data.token || '',
        user: data.user ? {
          id: data.user.id,
          username: data.user.email,
          role: data.user.is_global_admin ? 'admin' : 'editor',
          permissions: {},
        } as User : {} as User,
      };
    } catch (error: any) {
      return { success: false, token: '', user: {} as User, error: error.message || 'Login failed' };
    }
  },

  logout: async () => {
    try {
      await authFetch('logout', { method: 'POST' });
    } catch (e) { /* ignore */ }
    authService.clearStorage();
  },

  clearStorage: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(ORG_KEY);
    localStorage.removeItem(ROLES_KEY);
    localStorage.removeItem(PERMS_KEY);
    localStorage.removeItem('app_user_compat');
  },

  getSession: async (): Promise<SessionResponse> => {
    return authFetch<SessionResponse>('session');
  },

  refreshSession: async () => {
    try {
      const data = await authService.getSession();
      if (data.success && data.user) {
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        if (data.organization) localStorage.setItem(ORG_KEY, JSON.stringify(data.organization));
        if (data.roles) localStorage.setItem(ROLES_KEY, JSON.stringify(data.roles));
        if (data.permissions) localStorage.setItem(PERMS_KEY, JSON.stringify(data.permissions));
      }
      return data;
    } catch {
      return { success: false };
    }
  },

  // ═══ GETTERS (from localStorage) ══════════════
  getToken: (): string | null => localStorage.getItem(TOKEN_KEY),

  getUser: (): AuthUser | null => {
    const s = localStorage.getItem(USER_KEY);
    return s ? JSON.parse(s) : null;
  },

  getOrganization: (): AuthOrganization | null => {
    const s = localStorage.getItem(ORG_KEY);
    return s ? JSON.parse(s) : null;
  },

  getRoles: (): AuthRole[] => {
    const s = localStorage.getItem(ROLES_KEY);
    return s ? JSON.parse(s) : [];
  },

  getPermissions: (): Record<string, string[]> => {
    const s = localStorage.getItem(PERMS_KEY);
    return s ? JSON.parse(s) : {};
  },

  isLoggedIn: (): boolean => !!localStorage.getItem(TOKEN_KEY),

  isGlobalAdmin: (): boolean => {
    const user = authService.getUser();
    return user?.is_global_admin === true;
  },

  isOrgAdmin: (): boolean => {
    const roles = authService.getRoles();
    return roles.some(r => r.role_type === 'org_admin' || r.slug === 'org_admin') || authService.isGlobalAdmin();
  },

  hasPermission: (section: string, permission: string): boolean => {
    if (authService.isGlobalAdmin()) return true;
    const perms = authService.getPermissions();
    const sectionPerms = perms[section] || perms[`grc/${section}`] || [];
    return sectionPerms.includes(permission);
  },

  // Backward compat
  getCurrentUser: async (): Promise<User | null> => {
    const user = authService.getUser();
    if (!user) return null;
    const roles = authService.getRoles();
    return {
      id: user.id,
      username: user.email,
      role: user.is_global_admin ? 'admin' : (roles.some(r => r.role_type === 'org_admin') ? 'admin' : 'editor'),
      permissions: {},
    };
  },

  // ═══ PASSWORD MANAGEMENT ═════════════════════
  changePassword: async (currentPassword: string, newPassword: string) => {
    return authFetch<{ success: boolean; message?: string; error?: string }>('change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  },

  resetPassword: async (userId: number, newPassword: string) => {
    return authFetch<{ success: boolean; message?: string; error?: string }>('reset-password', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, new_password: newPassword }),
    });
  },

  // ═══ USER MANAGEMENT (org admin) ═════════════
  getUsers: async (orgId?: number) => {
    const url = orgId ? `users&org=${orgId}` : 'users';
    const res = await authFetch<{ success: boolean; data: OrgUser[] }>(url);
    return res.data || [];
  },

  createUser: async (data: { email: string; first_name: string; last_name?: string; password: string; role_id?: number }) => {
    return authFetch<{ success: boolean; id?: number; error?: string }>('create-user', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateUser: async (userId: number, data: Record<string, any>) => {
    return authFetch<{ success: boolean; error?: string }>('update-user', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, ...data }),
    });
  },

  toggleUser: async (userId: number, isActive: boolean) => {
    return authFetch<{ success: boolean; error?: string }>('toggle-user', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, is_active: isActive ? 1 : 0 }),
    });
  },

  assignRole: async (userId: number, roleId: number) => {
    return authFetch<{ success: boolean; error?: string }>('assign-role', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, role_id: roleId }),
    });
  },

  // ═══ ROLES & PERMISSIONS ═════════════════════
  getRolesList: async (orgId?: number) => {
    const url = orgId ? `roles&org=${orgId}` : 'roles';
    const res = await authFetch<{ success: boolean; data: any[] }>(url);
    return res.data || [];
  },

  createRole: async (name: string, description?: string) => {
    return authFetch<{ success: boolean; id?: number; error?: string }>('create-role', {
      method: 'POST',
      body: JSON.stringify({ name, description }),
    });
  },

  deleteRole: async (roleId: number) => {
    return authFetch<{ success: boolean; error?: string }>('delete-role', {
      method: 'POST',
      body: JSON.stringify({ role_id: roleId }),
    });
  },

  getModules: async () => {
    const res = await authFetch<{ success: boolean; data: any[] }>('modules');
    return res.data || [];
  },

  getPermissionsMatrix: async (roleId: number) => {
    return authFetch<{ success: boolean; permission_types: any[]; permissions: Record<string, Record<number, boolean>> }>(`permissions&role_id=${roleId}`);
  },

  setPermissions: async (roleId: number, permissions: Array<{ module_id: number; section_id?: number; permission_type_id: number }>) => {
    return authFetch<{ success: boolean; error?: string }>('set-permissions', {
      method: 'POST',
      body: JSON.stringify({ role_id: roleId, permissions }),
    });
  },

  // ═══ AUDIT TRAIL ═════════════════════════════
  getAuditLog: async (page = 1, limit = 50, actionFilter?: string, userFilter?: number, entityType?: string, source: 'all' | 'rbac' | 'grc' = 'all', orgId?: number) => {
    let url = `audit-log&page=${page}&limit=${limit}`;
    if (actionFilter) url += `&action_filter=${encodeURIComponent(actionFilter)}`;
    if (userFilter) url += `&user_filter=${userFilter}`;
    if (entityType) url += `&entity_type=${encodeURIComponent(entityType)}`;
    if (source !== 'all') url += `&source=${source}`;
    if (orgId) url += `&org_id=${orgId}`;
    return authFetch<{ success: boolean; data: AuditEntry[]; total: number; page: number; pages: number }>(url);
  },

  // ═══ GLOBAL ADMIN ════════════════════════════
  getOrganizations: async () => {
    const res = await authFetch<{ success: boolean; data: Organization[] }>('organizations');
    return res.data || [];
  },

  createOrganization: async (data: Record<string, any>) => {
    return authFetch<{ success: boolean; id?: number; error?: string }>('create-organization', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateOrganization: async (orgId: number, data: Record<string, any>) => {
    return authFetch<{ success: boolean; error?: string }>('update-organization', {
      method: 'POST',
      body: JSON.stringify({ organization_id: orgId, ...data }),
    });
  },

  setOrgApiKey: async (orgId: number, apiKey: string, model = 'gpt-4o') => {
    return authFetch<{ success: boolean; error?: string }>('set-org-api-key', {
      method: 'POST',
      body: JSON.stringify({ organization_id: orgId, api_key: apiKey, model }),
    });
  },

  getGlobalAuditLog: async (page = 1, limit = 50) => {
    return authFetch<{ success: boolean; data: AuditEntry[]; total: number; page: number; pages: number }>(`global-audit-log&page=${page}&limit=${limit}`);
  },

  getGlobalUsers: async () => {
    const res = await authFetch<{ success: boolean; data: any[] }>('global-users');
    return res.data || [];
  },

  deleteOrganization: async (orgId: number) => {
    return authFetch<{ success: boolean; error?: string }>('delete-organization', {
      method: 'POST',
      body: JSON.stringify({ organization_id: orgId }),
    });
  },

  createOrgAdmin: async (orgId: number, data: { email: string; first_name: string; last_name?: string; password: string }) => {
    return authFetch<{ success: boolean; user_id?: number; error?: string }>('create-org-admin', {
      method: 'POST',
      body: JSON.stringify({ organization_id: orgId, ...data }),
    });
  },

  getOrgUsers: async (orgId: number) => {
    const res = await authFetch<{ success: boolean; data: any[] }>(`org-users&organization_id=${orgId}`);
    return res.data || [];
  },

  getOrgRoles: async (orgId: number) => {
    const res = await authFetch<{ success: boolean; data: any[] }>(`org-roles&organization_id=${orgId}`);
    return res.data || [];
  },

  // ═══ BACKWARD COMPAT ═════════════════════════
  getGroups: async () => {
    try {
      const roles = await authService.getRolesList();
      return roles.map((r: any) => ({ id: r.id, name: r.name, permissions: {} }));
    } catch {
      return [];
    }
  },

  createGroup: async (group: any) => authService.createRole(group.name, group.description),
  updateGroup: async (_id: number, _group: any) => ({ success: true }),
  deleteGroup: async (_id: number) => ({ success: true }),
  
  // Permanently delete a user (not just deactivate)
  deleteUser: async (id: number) => {
    return authFetch<{ success: boolean; error?: string }>('delete-user', {
      method: 'POST',
      body: JSON.stringify({ user_id: id }),
    });
  },
};
